import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { InputLabel } from '../ui/InputLabel';
import { useTranslation } from 'react-i18next';
import { CircleCheck, Circle } from 'lucide-react';

type FormValues = {
  password: string;
  confirmPassword: string;
};

export default function NewPasswordForm({
  onSubmit,
  loading,
  formError,
}: Readonly<{
  onSubmit?: (data: { password: string }) => Promise<void> | void;
  loading?: boolean;
  formError?: string | null;
}>) {
  const { t } = useTranslation(['auth', 'common']);

  const schema = useMemo(
    () =>
      z
        .object({
          password: z
            .string()
            .min(12)
            .max(30)
            .refine((val) => /[a-z]/.test(val))
            .refine((val) => /[A-Z]/.test(val))
            .refine((val) => /\d/.test(val))
            .refine((val) => /[^A-Za-z0-9]/.test(val)),
          confirmPassword: z.string(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('auth:errors.passwordMismatch'),
          path: ['confirmPassword'],
        }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const isValid = form.formState.isValid;
  const passwordValue = form.watch('password');
  const meetsLength = passwordValue.length >= 12;
  const hasLowercase = /[a-z]/.test(passwordValue);
  const hasUppercase = /[A-Z]/.test(passwordValue);
  const hasDigit = /\d/.test(passwordValue);
  const hasSymbol = /[^A-Za-z0-9]/.test(passwordValue);

  return (
    <form
      onSubmit={form.handleSubmit(
        async (data) => onSubmit && (await onSubmit({ password: data.password })),
      )}
    >
      <div className="space-y-6">
        <div className="space-y-1">
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:newPassword')}
                id="password"
                type="password"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  // Le refine croisé password/confirmPassword ne se revalide pas tout seul côté
                  // RHF quand on retouche password après avoir déjà rempli confirmPassword : on
                  // force la revalidation dans ce cas pour garder l'erreur à jour immédiatement.
                  if (form.getValues('confirmPassword')) {
                    void form.trigger('confirmPassword');
                  }
                }}
              />
            )}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
            {[
              { met: meetsLength, label: t('auth:passwordRequirements.length') },
              { met: hasLowercase, label: t('auth:passwordRequirements.lowercase') },
              { met: hasUppercase, label: t('auth:passwordRequirements.uppercase') },
              { met: hasDigit, label: t('auth:passwordRequirements.number') },
              { met: hasSymbol, label: t('auth:passwordRequirements.symbol') },
            ].map((req) => (
              <div
                key={req.label}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-200 ${
                  req.met ? 'bg-success/15 text-success' : 'bg-white/10 text-tertiary-inverse'
                }`}
              >
                {req.met ? (
                  <CircleCheck className="size-3.5 shrink-0" />
                ) : (
                  <Circle className="size-3.5 shrink-0" />
                )}
                <span className="truncate">{req.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <Controller
            name="confirmPassword"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:confirmPassword')}
                id="confirmPassword"
                type="password"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.confirmPassword && (
            <p className="text-xs text-error-400">
              {form.formState.errors.confirmPassword.message}
            </p>
          )}
          {formError ? <p className="text-xs text-error-400">{formError}</p> : null}
        </div>

        <Button
          type="submit"
          className="w-full h-11 rounded-full text-base disabled:bg-button-primary-disable-inverse disabled:text-disable"
          disabled={!isValid || loading}
        >
          {t('auth:save')}
        </Button>
      </div>
    </form>
  );
}
